import { Helmet } from "react-helmet-async";

/**
 * Reusable SEO component. Applies per-route meta tags + JSON-LD on top of the
 * static defaults declared in /public/index.html. Always pass at minimum a
 * `title` and `description`. Optional: `path`, `image`, `type`, `jsonLd`.
 */
const BASE_URL = "https://debate-engine.com";
const DEFAULT_IMAGE = `${BASE_URL}/logo.png`;

export const SEO = ({
  title,
  description,
  path = "",
  image = DEFAULT_IMAGE,
  type = "website",
  jsonLd = null,
  noindex = false,
}) => {
  const url = `${BASE_URL}${path}`;
  const fullTitle = title.includes("Debate Engine") ? title : `${title} | Debate Engine`;
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      {/* Structured data */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

/** Pre-built JSON-LD generators. */
export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${BASE_URL}${it.path}`,
  })),
});

export const faqLd = (qaPairs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: qaPairs.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
});

export const productLd = ({ name, description, price = "0", currency = "EUR" }) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name,
  description,
  brand: { "@type": "Brand", name: "Debate Engine" },
  offers: { "@type": "Offer", price, priceCurrency: currency, availability: "https://schema.org/InStock" },
});

export const articleLd = ({ headline, description, datePublished, url }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline,
  description,
  datePublished,
  url,
  publisher: {
    "@type": "Organization",
    name: "Debate Engine",
    logo: { "@type": "ImageObject", url: DEFAULT_IMAGE },
  },
});

export default SEO;
